import { useState, useRef, memo, useEffect, forwardRef, useImperativeHandle } from 'react';
import { useToast } from '@/hooks/use-toast';
import { useMutation, useQuery } from '@tanstack/react-query';
import { Button } from '@/components/ui/button';
import { GameTimestamp } from '@shared/types/schema';
import { HomeGame } from '@/types/game';
import { cn } from '@/lib/utils';
import { ChevronsLeft, Loader2 } from 'lucide-react';
import { ChevronLeft } from 'lucide-react';
import { ChevronRight } from 'lucide-react';
import { useHls } from '@/hooks/use-hls';
import CrossFilled from '@icons/cross-filled.svg?react';
import axiosClient from '@/lib/axiosClient';
import { queryClient } from '@/lib/queryClient';
import { useHotkeys } from 'react-hotkeys-hook';
import { useLockBodyScroll } from 'react-use';
import { useAsyncThrottler } from '@tanstack/react-pacer';
import {
  DropdownMenu,
  DropdownMenuTrigger,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuPortal,
} from '@/components/ui/dropdown-menu';
import { Check, Settings, Play, Pause, VolumeX, Volume1, Maximize2 } from 'lucide-react';

export type GetVideoHandle = {
  playVideo(): void;
};

const GetVideoInner = forwardRef(
  (
    {
      game,
      signedUrl,
      isGameLaunched,
      isGamePurchased,
      className,
      initialTime,
      setShowVideo,
    }: {
      game: HomeGame;
      signedUrl: string | null;
      isGameLaunched: boolean;
      isGamePurchased: boolean;
      className?: string;
      initialTime?: number;
      setShowVideo: React.Dispatch<React.SetStateAction<boolean>>;
    },
    ref,
  ) => {
    const videoRef = useRef<HTMLVideoElement>(null);
    const [currentTime, setCurrentTime] = useState(0);
    const [prevTimestamp, setPrevTimestamp] = useState<GameTimestamp | null>(null);
    const [prevPrevTimestamp, setPrevPrevTimestamp] = useState<GameTimestamp | null>(null); // new state
    const [nextTimestamp, setNextTimestamp] = useState<GameTimestamp | null>(null);
    const [showVideoControls, setShowVideoControls] = useState(false);
    const [isVideoLoading, setIsVideoLoading] = useState(false);
    const [isVideoFullscreen, setIsVideoFullscreen] = useState(false);

    const saveProgress = useMutation({
      mutationFn: async (time: number) => {
        await axiosClient.post(`/api/videos/progress`, {
          progressSeconds: Math.floor(time),
          gameId: game.id,
          videoId: game.videoId,
        });
      },
      onSuccess: () => {
        queryClient.invalidateQueries({
          queryKey: [
            '/api/videos/progress?' +
              new URLSearchParams({ gameId: game?.id, videoId: game?.videoId }),
          ],
        });
      },
    });

    const saveProgressThrottled = useAsyncThrottler(
      async (time: number) => {
        await saveProgress.mutateAsync(time);
      },
      { wait: 30000 },
    );

    useImperativeHandle(ref, () => {
      return {
        playVideo() {
          videoRef.current?.play();
        },
      };
    });

    const closeVideo = () => {
      setShowVideo(false);
    };

    useHotkeys('Escape', closeVideo);

    const handlePause = () => {
      saveProgressThrottled.maybeExecute(currentTime);
    };

    useEffect(() => {
      saveProgressThrottled.maybeExecute(currentTime);
    }, [currentTime]);

    // TODO: Set video to last remembered timestamp (for now, hardcoded)
    const setToRememberedTimestamp = () => {
      if (videoRef.current) {
        videoRef.current.currentTime = 42;
        setCurrentTime(42);
      }
    };

    const { toast } = useToast();

    const launchMutation = useMutation({
      mutationFn: async () => {
        const res = await axiosClient.post(`/api/games/${game.id}/launch`);
        return res.data;
      },
      onSuccess: () => {
        if (!game.isFree) {
          queryClient.invalidateQueries({
            queryKey: ['/api/games', game.id, 'launches'],
          });
        }
        queryClient.invalidateQueries({
          queryKey: [`/api/games/${game.id}/rating`],
        });
      },
      onError: (error: Error) => {
        toast({
          title: 'Ошибка запуска',
          description: error.message,
          variant: 'destructive',
        });
        closeVideo();
      },
    });

    const onGameLaunched = () => {
      launchMutation.mutate();
    };

    const { data: timestamps = [] } = useQuery<Array<GameTimestamp>>({
      queryKey: [`/api/games/${game.id}/timestamps`],
      enabled: !!game.id && !isNaN(game.id),
    });

    const findNearestTimestamp = (direction: 'next' | 'prev') => {
      if (!timestamps.length) return null;

      const sortedTimestamps = [...timestamps].sort((a, b) => a.timestamp - b.timestamp);

      if (direction === 'next') {
        return sortedTimestamps.find((ts) => ts.timestamp > currentTime);
      } else {
        // For "prev", iterate from the end to get the closest timestamp that is less than currentTime.
        return sortedTimestamps
          .slice()
          .reverse()
          .find((ts) => ts.timestamp < currentTime);
      }
    };

    // New helper: finds the timestamp before the nearest previous one.
    const findPrevPrevTimestamp = () => {
      if (!timestamps.length) return null;
      const sortedTimestamps = [...timestamps].sort((a, b) => a.timestamp - b.timestamp);
      // Iterate backwards to find the first timestamp less than currentTime
      let prevIndex = -1;
      for (let i = sortedTimestamps.length - 1; i >= 0; i--) {
        if (sortedTimestamps[i].timestamp < currentTime) {
          prevIndex = i;
          break;
        }
      }
      // If there is a timestamp before the found one, return it
      return prevIndex > 0 ? sortedTimestamps[prevIndex - 1] : null;
    };

    const handleTimeUpdate = () => {
      const video = videoRef.current;
      if (!video) return;
      setCurrentTime(video.currentTime);
    };

    const initVideoPlayer = () => {
      setShowVideoControls(true);
      setIsVideoLoading(false);
    };

    useEffect(() => {
      const video = videoRef.current;
      if (!video) return;

      if (isSafariBrowser()) {
        setIsVideoMuted(true);
        video.defaultMuted = true;
        video.muted = true;
      }

      video.addEventListener('timeupdate', handleTimeUpdate);
      video.addEventListener('canplay', initVideoPlayer);
      video.addEventListener('play', initVideoPlayer);

      video.addEventListener('waiting', () => setIsVideoLoading(true));

      if (video.id === 'game-video-player') {
        video.addEventListener('canplay', togglePlay);
      }
      return () => {
        video.removeEventListener('timeupdate', handleTimeUpdate);
        video.removeEventListener('canplay', togglePlay);
        video.removeEventListener('canplay', initVideoPlayer);
        video.removeEventListener('waiting', () => setIsVideoLoading(true));
      };
    }, [videoRef.current, isGamePurchased]);

    useEffect(() => {
      setIsVideoLoading(isGameLaunched);
    }, [isGameLaunched]);

    useEffect(() => {
      setPrevTimestamp(findNearestTimestamp('prev') ?? null);
      setNextTimestamp(findNearestTimestamp('next') ?? null);
      setPrevPrevTimestamp(findPrevPrevTimestamp() ?? null);
    }, [currentTime, timestamps]);

    const handleNavigation = (direction: 'next' | 'prev') => {
      const timestamp = findNearestTimestamp(direction);
      if (timestamp && videoRef.current) {
        videoRef.current.currentTime = timestamp.timestamp;
        setCurrentTime(timestamp.timestamp);
        toast({
          title: timestamp.label,
          description: `${Math.floor(timestamp.timestamp / 60)}:${String(
            Math.floor(timestamp.timestamp % 60),
          ).padStart(2, '0')}`,
        });
      }
    };

    // New handler for "prev prev" navigation.
    const handleNavigationPrevPrev = () => {
      const timestamp = findPrevPrevTimestamp();
      if (timestamp && videoRef.current) {
        videoRef.current.currentTime = timestamp.timestamp;
        setCurrentTime(timestamp.timestamp);
        toast({
          title: timestamp.label,
          description: `${Math.floor(timestamp.timestamp / 60)}:${String(
            Math.floor(timestamp.timestamp % 60),
          ).padStart(2, '0')}`,
        });
      }
    };

    const [volumOfVideo, setVolumOfVideo] = useState(25);
    const [isVideoPlay, setIsVideoPlay] = useState(false);
    const [isVideoMuted, setIsVideoMuted] = useState(false);

    const togglePlay = () => {
      if (!videoRef.current || videoRef.current?.error) return;

      setIsVideoPlay(!isVideoPlay);
      if (!isVideoPlay) {
        videoRef.current.play();
      } else {
        videoRef.current.pause();
      }
    };

    const volumebar = (e: React.ChangeEvent<HTMLInputElement>) => {
      if (!videoRef.current || videoRef.current?.error) return;

      const volumeValue = parseFloat(e.target.value) / 100;
      setVolumOfVideo(Number(e.target.value));
      videoRef.current.volume = volumeValue;
    };

    const toggleMute = () => {
      if (!videoRef.current || videoRef.current?.error) return;

      videoRef.current.muted = !videoRef.current.muted;
      setIsVideoMuted(videoRef.current.muted);
    };

    const toggleFullScreen = () => {
      if (!videoRef.current || videoRef.current?.error) return;
      const el = document.getElementById('video-container') as HTMLElement & {
        mozRequestFullScreen(): Promise<void>;
        webkitRequestFullscreen(): Promise<void>;
        msRequestFullscreen(): Promise<void>;
        webkitEnterFullScreen(): Promise<void>;
      };
      if (!el) return;

      if (document.fullscreenElement) {
        document.exitFullscreen();
        setIsVideoFullscreen(false);
      } else {
        setIsVideoFullscreen(true);

        const isSafari = isSafariBrowser();
        if (isSafari) {
          isSafari.safariVideoElement.webkitEnterFullScreen();
        }

        if (el.requestFullscreen) {
          el.requestFullscreen();
        } else if (el.webkitRequestFullscreen) {
          /* Safari */
          el.webkitRequestFullscreen();
        } else if (el.webkitEnterFullScreen) {
          el.webkitEnterFullScreen();
        } else if (el.msRequestFullscreen) {
          /* IE11 */
          el.msRequestFullscreen();
        } else if (el.mozRequestFullScreen) {
          /* Mozila */
          el.mozRequestFullScreen();
        }
      }
    };

    const isSafariBrowser = () => {
      const safariEl = document
        .getElementById('video-container')
        ?.getElementsByTagName('video')[0] as HTMLVideoElement & {
        webkitEnterFullScreen(): Promise<void>;
      };

      if (!safariEl.webkitEnterFullScreen) {
        return false;
      }

      return {
        safariVideoElement: safariEl,
      };
    };

    const { changeLevel, switching, selectedLevel, levels } = useHls(
      videoRef,
      signedUrl,
      isGameLaunched && !!signedUrl,
    );

    useLockBodyScroll(true);

    useEffect(() => {
      console.log('dependencies changed', initialTime, videoRef);
      if (!videoRef.current) return;
      videoRef.current.currentTime = initialTime ?? 0;
    }, [initialTime, videoRef.current]);

    return (
      <div
        id="video-container"
        className={cn([
          className,
          'absolute inset-0 overflow-hidden z-[2000] flex items-center justify-center bg-background',
        ])}
      >
        {game.videoId && isGameLaunched && (isGamePurchased || game.isFree) && signedUrl ? (
          <>
            <video
              id="game-video-player"
              ref={videoRef}
              controlsList="nodownload"
              onContextMenu={(e) => e.preventDefault()}
              playsInline
              autoPlay
              className="w-full h-full aspect-video"
              poster={game.thumbnailUrl ?? undefined}
              preload="metadata"
              onClick={togglePlay}
              onPause={handlePause}
              onLoadedMetadata={() => {
                onGameLaunched();
              }}
              onError={(e) => {
                console.error('Video error:', e);
                toast({
                  variant: 'destructive',
                  title: 'Ошибка воспроизведения',
                  description: 'Не удалось загрузить видео',
                });
              }}
            >
              <img src={game.thumbnailUrl ?? undefined} />
            </video>
            {timestamps.length > 0 && (
              <div
                className="absolute top-4 xl:bottom-12 xl:top-auto right-2 z-10 xl:right-1/3 xl:translate-x-1/3"
                onClick={(event) => event.stopPropagation()}
              >
                <div className="flex items-center sm:text-3xl text-base">
                  <Button
                    variant="secondary"
                    onClick={handleNavigationPrevPrev}
                    className="pointer-events-auto bg-black/70 backdrop-blur-md shadow-lg font-bold opacity-95 p-2 sm:p-[12px]"
                    data-goal-id="video_double_back_clicked"
                  >
                    <ChevronsLeft
                      className="sm:h-11 sm:w-11 h-5 w-5 fill-none"
                      style={{ fill: 'none' }}
                    />
                    Начало
                  </Button>
                  <Button
                    variant="secondary"
                    onClick={() => handleNavigation('prev')}
                    className={
                      (!prevTimestamp ? 'invisible ' : '') +
                      'pointer-events-auto bg-black/70 backdrop-blur-md shadow-lg font-bold opacity-95 ml-[2px] sm:ml-2 p-2 sm:p-[12px]'
                    }
                    data-goal-id="video_back_clicked"
                  >
                    <ChevronLeft className="sm:h-11 sm:w-11 h-5 w-5" style={{ fill: 'none' }} />
                    {prevTimestamp?.label}
                  </Button>

                  <Button
                    variant="secondary"
                    onClick={() => handleNavigation('next')}
                    className={
                      (!nextTimestamp ? 'invisible ' : '') +
                      ' pointer-events-auto bg-black/70 backdrop-blur-md shadow-lg font-bold opacity-95 ml-[1px] sm:ml-2 p-2 sm:p-[12px]'
                    }
                    data-goal-id="video_next_clicked"
                  >
                    {nextTimestamp?.label}
                    <ChevronRight className="sm:h-11 sm:w-11 h-5 w-5" style={{ fill: 'none' }} />
                  </Button>
                </div>
              </div>
            )}
          </>
        ) : game.trailerUrl && !isGamePurchased ? (
          <video
            src={game.trailerUrl + '#t=0.1'}
            ref={videoRef}
            playsInline
            className="w-full aspect-video"
            preload="metadata"
            poster={game.thumbnailUrl ?? undefined}
            onError={(e) => {
              console.error('Trailer error:', e);
              toast({
                variant: 'destructive',
                title: 'Ошибка воспроизведения',
                description: 'Не удалось загрузить трейлер',
              });
            }}
          />
        ) : (
          <img className="max-h-[400px] m-auto" src={game.thumbnailUrl ?? undefined} />
        )}

        {isVideoLoading && (
          <div className="absolute left-1/2 top-1/2 -translate-y-1/2 -translate-x-1/2 text-primary ">
            <Loader2 className="h-16 w-16 animate-spin" />
          </div>
        )}

        {showVideoControls && (
          <>
            {!isVideoPlay && (
              <>
                <div
                  className="bg-black/50 absolute w-full h-full top-0 left-0"
                  onClick={togglePlay}
                ></div>

                {game.heroImageUrl && (
                  <img
                    src={game.heroImageUrl}
                    className="absolute xl:top-6 sm:bottom-4 bottom-16 sm:max-w-[400px] max-w-[200px]"
                  />
                )}

                <div
                  className="absolute left-1/2 top-1/2 -translate-y-1/2 -translate-x-1/2 hover:[&_svg]:scale-110 inline-block cursor-pointer"
                  onClick={togglePlay}
                  data-goal-id="video_center_play_clicked"
                >
                  <div className="flex items-center justify-center flex-col">
                    <Play className="w-11 h-11" />
                    {!isGameLaunched && <span className="text-sm">Посмотреть трейлер</span>}
                  </div>
                </div>
              </>
            )}

            <div className="absolute left-0 bottom-0 flex gap-2 items-center justify-between w-full xl:py-11 py-2 xl:px-16 px-3">
              <div className="flex gap-2">
                <div
                  className="hover:[&_svg]:scale-110 inline-block cursor-pointer p-3"
                  onClick={togglePlay}
                  data-goal-id={!isVideoPlay ? 'video_play_clicked' : 'video_pause_clicked'}
                >
                  {!isVideoPlay ? <Play className="w-11 h-11" /> : <Pause className="w-11 h-11" />}
                </div>
                <div className="flex group items-center">
                  {!isVideoMuted ? (
                    <>
                      <Volume1
                        className="hover:scale-110 cursor-pointer w-11 h-11"
                        onClick={toggleMute}
                        data-goal-id="video_mute_clicked"
                      />
                      <input
                        className="opacity-0 group-hover:opacity-100 transition duration-150 accent-primary h-1 max-w-[75px]"
                        type="range"
                        min="0"
                        max="100"
                        step="10"
                        value={volumOfVideo}
                        onChange={volumebar}
                      />
                    </>
                  ) : (
                    <VolumeX
                      className="hover:scale-110 cursor-pointer w-11 h-11"
                      onClick={toggleMute}
                      data-goal-id="video_unmute_clicked"
                    />
                  )}
                </div>
              </div>

              <div className="flex gap-2">
                <DropdownMenu modal={false}>
                  <DropdownMenuTrigger
                    disabled={switching}
                    asChild
                    onFocusCapture={(event) => {
                      event.stopPropagation();
                    }}
                  >
                    <div className="p-3">
                      {switching ? (
                        <div className="absolute left-1/2 top-1/2 -translate-y-1/2 -translate-x-1/2 text-primary ">
                          <Loader2 className="w-11 h-11 animate-spin" />
                        </div>
                      ) : (
                        <Settings className="w-11 h-11 hover:scale-110 cursor-pointer" />
                      )}
                    </div>
                  </DropdownMenuTrigger>

                  <DropdownMenuPortal>
                    <DropdownMenuContent align="start" className="w-36 z-[2001]">
                      <DropdownMenuItem
                        className="flex items-center justify-between"
                        onSelect={() => changeLevel(-1)}
                      >
                        Auto
                        {selectedLevel === -1 && <Check className="w-4 h-4" />}
                      </DropdownMenuItem>

                      {levels.map(({ height, idx }) => (
                        <DropdownMenuItem
                          key={idx}
                          className="flex items-center justify-between"
                          onSelect={() => changeLevel(idx)}
                        >
                          {height}p{selectedLevel === idx && <Check className="w-4 h-4" />}
                        </DropdownMenuItem>
                      ))}
                    </DropdownMenuContent>
                  </DropdownMenuPortal>
                </DropdownMenu>

                <div
                  className="p-3"
                  onClick={toggleFullScreen}
                  onTouchEnd={toggleFullScreen}
                  data-goal-id="video_fullscreen_clicked"
                >
                  <Maximize2 className="w-11 h-11 hover:scale-110 cursor-pointer" />
                </div>
              </div>
            </div>
            <div className="absolute left-0 top-0 flex gap-2 items-center justify-between w-full xl:py-11 py-2 xl:px-16 px-3">
              <button
                onClick={closeVideo}
                className="size-16 p-3"
                data-goal-id="close_video_clicked"
              >
                <CrossFilled className="fill-white/90 hover:fill-white transition-colors duration-300" />
              </button>
            </div>
          </>
        )}
      </div>
    );
  },
);

const GetVideo = memo(GetVideoInner);
export { GetVideo };
